import isString from 'lodash/isString';
import startCase from 'lodash/startCase';

import { metaPromotedFieldsToVerbose } from '@/app/instagram/constants';

export type VerboseField = string | { label: string; nested?: Record<string, string> };
export type VerboseFields = Record<string, VerboseField>;

interface MetaAdsAccountLike {
  account_name?: string;
  currency?: string;
}

interface AdAccountScopedInsight {
  adAccountId: string | number;
  [key: string]: unknown;
}

export function formatMetaPromoted(
  field: string,
  {
    parentField,
    verboseFields = metaPromotedFieldsToVerbose as VerboseFields,
  }: { parentField?: string; verboseFields?: VerboseFields } = {},
): string {
  const parentVerbose = parentField ? verboseFields[parentField] : undefined;
  if (parentVerbose && !isString(parentVerbose) && parentVerbose.nested?.[field]) {
    return parentVerbose.nested[field];
  }
  const verbose = verboseFields[field];
  if (verbose) {
    return isString(verbose) ? verbose : verbose.label;
  }
  if (field === 'videoViewsPaid') {
    return 'Plays - Promoted';
  }
  if (field.startsWith('promoted_scoped')) {
    const [adAccountLabel] = field.split('_').slice(3);
    return `Promoted - ${adAccountLabel}`;
  }
  if (!field.endsWith('Paid')) {
    return startCase(field.replaceAll(/([A-Z])/g, ' $1').replaceAll(/([_])/g, ' '));
  }
  return startCase(field).replace(
    ' Paid',
    field.startsWith('videoPlays') ? '% - Promoted' : ' - Promoted',
  );
}

export function getFormattedNestings(
  nestings: string[],
  values: Record<string, unknown>,
): Record<string, unknown> {
  if (nestings.length === 2) {
    const [organicField, promotedField] = nestings;
    return {
      organic: values[organicField] ?? '',
      promoted: values[promotedField] ?? '',
    };
  }
  const formattedNestings: Record<string, unknown> = {};
  nestings.forEach((subField) => {
    formattedNestings[subField] = values[subField] ?? '';
  });
  return formattedNestings;
}

export const getAdAccountLabel = (
  adAccountId: string | number,
  adsAccounts: Record<string, MetaAdsAccountLike> | null | undefined,
) => adsAccounts?.[adAccountId]?.account_name ?? `Ad Account ID: ${adAccountId}`;

export function getAdAccountScopedNestings(
  adAccountScopedInsights: AdAccountScopedInsight[],
  metaAdsAccounts: Record<string, MetaAdsAccountLike> | null | undefined,
  field: string,
  defaultValues: Record<string, unknown> = {},
): Record<string, unknown> {
  return adAccountScopedInsights.reduce(
    (acc, insights) => {
      const adAccountLabel = getAdAccountLabel(insights.adAccountId, metaAdsAccounts);
      acc[`promoted_scoped_${field}_${adAccountLabel}`] = insights?.[field];
      return acc;
    },
    { ...defaultValues },
  );
}

/**
 * Returns the currency of the first Meta Ads account. Accepts either a record keyed by account id
 * or an array of accounts.
 */
export function getCurrencyFromMetaAdsAccounts(
  metaAdsAccounts: Record<string, MetaAdsAccountLike> | MetaAdsAccountLike[] | null | undefined,
): string | undefined {
  return Object.values(metaAdsAccounts ?? {})[0]?.currency;
}

export function sortMetaPromotedFields(
  a: string,
  b: string,
  formatter: (field: string) => string = formatMetaPromoted,
): number {
  if (a === 'likeshopClicks') return 1;
  if (b === 'likeshopClicks') return -1;
  const aFormatted = formatter(a);
  const bFormatted = formatter(b);
  return aFormatted.localeCompare(bFormatted, undefined, { numeric: true });
}
